import { Circle } from "lucide-react";

const HEALTH = {
  ok: { color: "#10b981", label: "matrix.health.ok" },
  watch: { color: "#f59e0b", label: "matrix.health.watch" },
  risk: { color: "#ef4444", label: "matrix.health.risk" },
};
const SYNC = {
  synced: { color: "#10b981", label: "matrix.sync.synced" },
  error: { color: "#ef4444", label: "matrix.sync.error" },
  planned: { color: "#71717a", label: "matrix.sync.planned" },
};

function Th({ children, align }) {
  const a = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  return <th className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${a} first:pl-5 last:pr-5`}>{children}</th>;
}

export function PortalHealthMatrix({ portals = [], t, onRow }) {
  const chf = (n) => `CHF ${(n ?? 0).toLocaleString("de-CH")}`;
  return (
    <div data-testid="portal-health-matrix" className="overflow-hidden rounded-lg border border-zinc-800/60 bg-[#18181B]">
      <div className="flex items-center justify-between border-b border-zinc-800/60 px-5 py-4">
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-100">{t("matrix.title")}</h2>
          <p className="mt-0.5 truncate text-xs text-zinc-500">{t("matrix.sub")}</p>
        </div>
        <span className="ml-3 shrink-0 rounded-full border border-zinc-700 px-2.5 py-1 text-xs font-medium text-zinc-400">{portals.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-zinc-800 bg-[#18181B]">
              <Th align="left">{t("matrix.col.portal")}</Th>
              <Th align="center">{t("matrix.col.status")}</Th>
              <Th align="right">{t("matrix.col.providers")}</Th>
              <Th align="right">{t("matrix.col.approvals")}</Th>
              <Th align="right">{t("matrix.col.inquiries")}</Th>
              <Th align="right">{t("matrix.col.packages")}</Th>
              <Th align="right">{t("matrix.col.invoices")}</Th>
              <Th align="right">{t("matrix.col.revenue")}</Th>
              <Th align="center">{t("matrix.col.sync")}</Th>
            </tr>
          </thead>
          <tbody>
            {portals.length === 0 && (
              <tr><td colSpan={9} className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="matrix-empty">{t("matrix.empty")}</td></tr>
            )}
            {portals.map((p) => {
              const live = p.status === "live";
              const h = HEALTH[p.health] || HEALTH.ok;
              const s = SYNC[p.wp_sync] || SYNC.planned;
              const overdue = p.overdue || 0;
              return (
                <tr
                  key={p.portal_key}
                  data-testid={`matrix-row-${p.portal_key}`}
                  onClick={() => onRow && onRow(p.portal_key)}
                  className="cursor-pointer border-b border-zinc-800/60 transition-colors duration-100 last:border-0 hover:bg-white/[0.03]"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span
                        data-testid={`matrix-health-${p.portal_key}`}
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: h.color, boxShadow: `0 0 8px ${h.color}66` }}
                        aria-label={`${t("matrix.col.health")}: ${t(h.label)}`}
                        title={t(h.label)}
                      />
                      <div className="min-w-0">
                        <div className="font-heading text-sm font-semibold leading-tight text-zinc-100">{p.name}</div>
                        <div className="text-[11px] text-zinc-500">{p.portal_key}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center" data-testid={`matrix-cell-${p.portal_key}-status`}>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                      style={live
                        ? { color: "#34d399", borderColor: "rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.08)" }
                        : { color: "#a1a1aa", borderColor: "#3f3f46", background: "rgba(255,255,255,0.02)" }}
                    >
                      <Circle className="h-1.5 w-1.5 fill-current" />
                      {live ? t("control.live") : t("control.skeleton")}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="font-medium text-zinc-100">{p.providers}</span>
                    {p.new_7d > 0 && <span className="ml-1 text-[11px] text-emerald-400">+{p.new_7d}</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums" style={{ color: p.pending > 0 ? "#f59e0b" : "#e4e4e7" }}>{p.pending}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-zinc-200">{p.open}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-zinc-200">{p.subscriptions}</td>
                  <td className="px-3 py-3 text-right tabular-nums" style={overdue > 0 ? { color: "#ef4444", fontWeight: 700 } : { color: "#e4e4e7" }}>
                    {p.open_invoices}{overdue > 0 ? ` / ${overdue}` : ""}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-zinc-100" data-testid={`matrix-cell-${p.portal_key}-revenue`}>{chf(p.revenue)}</td>
                  <td className="px-3 py-3 pr-5 text-center" data-testid={`matrix-cell-${p.portal_key}-sync`}>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: s.color }} title={t(s.label)} aria-label={`WP Sync: ${t(s.label)}`}>
                      <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                      <span className="hidden sm:inline">{t(s.label)}</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
